<template>
    <div>
        <label id="listbox-label" class="block text-sm font-medium text-gray-700" v-if="title">
            {{ title }}
        </label>
        <div class="mt-1 relative z-10">
            <button
                type="button"
                aria-haspopup="listbox"
                :aria-expanded="open"
                aria-labelledby="listbox-label"
                class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                @click="toggle"
            >
                <span class="block truncate">
                    {{ selectedLabel || 'Choose' }}
                </span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <!-- Heroicon name: selector -->
                    <svg
                        class="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        :aria-hidden="!open"
                    >
                        <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </span>
            </button>

            <transition
                enter-active-class=""
                enter-class=""
                enter-to-class=""
                leave-active-class="transition ease-in duration-100"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div class="absolute mt-1 w-full rounded-md bg-white shadow-lg" v-show="open">
                    <ul
                        tabindex="-1"
                        role="listbox"
                        aria-labelledby="listbox-label"
                        aria-activedescendant="listbox-item-3"
                        class="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                    >
                        <li
                            :id="`listbox-option-${index}`"
                            role="option"
                            class="cursor-default select-none relative py-2 pl-3 pr-9"
                            :class="{
                                'text-white bg-indigo-600 hover:bg-indigo-400': optionIsSelected(option),
                                'text-gray-900 hover:bg-gray-100': !optionIsSelected(option),
                            }"
                            :key="index"
                            @click="onSelect(option)"
                            v-for="(option, index) in options"
                        >
                            <span class="font-normal block truncate">
                                {{ option[normalizedLabel] }}
                            </span>
                            <span class="text-white absolute inset-y-0 right-0 flex items-center pr-4" v-if="optionIsSelected(option)">
                                <!-- Heroicon name: check -->
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </li>
                    </ul>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            title: {
                type: String,
                default: '',
            },
            identifier: {
                type: String,
                default: 'value',
            },
            label: {
                type: String,
                default: 'label',
            },
            value: {
                type: [String, Array],
                default: () => [],
            },
            options: {
                type: Array,
                default: () => [],
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            allowEmpty: {
                type: Boolean,
                default: true,
            },
        },

        data() {
            return {
                open: false,
                selected: [],
            };
        },

        computed: {
            selectedLabel() {
                if (!this.selected) {
                    return '';
                }

                const optionSelected = this.options
                    .filter(option => {
                        return this.selected.includes(option[this.normalizedIdentifier]);
                    })
                    .map(option => option[this.normalizedLabel]);

                return optionSelected.join(', ');
            },

            normalizedIdentifier() {
                return this.identifier || 'id';
            },

            normalizedLabel() {
                return this.label || 'label';
            },
        },

        watch: {
            value: {
                immediate: true,
                handler() {
                    this.initSelection();
                },
            },
        },

        methods: {
            onSelect(option) {
                if (!this.allowEmpty && this.selected.length == 1 && this.optionIsSelected(option)) {
                    return;
                }

                this.toggle();

                const optionValue = option[this.normalizedIdentifier];

                if (this.optionIsSelected(option)) {
                    this.selected.splice(this.selected.indexOf(optionValue), 1);
                } else if (this.multiple) {
                    this.selected.push(optionValue);
                } else {
                    this.selected = [optionValue];
                }

                this.$emit('input', this.selected);
            },

            optionIsSelected(option) {
                return this.selected.includes(option[this.normalizedIdentifier]);
            },

            toggle() {
                this.open = !this.open;
            },

            initSelection() {
                if (typeof this.value == 'string') {
                    this.selected = [this.value];
                    return;
                }

                this.selected = this.value;
            },
        },
    };
</script>
